import { cloneElement, useEffect, useMemo, useState } from 'react';
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs';
import { DropDownList, DropDownListChangeEvent } from '@progress/kendo-react-dropdowns';
import { TextArea, TextAreaChangeEvent } from '@progress/kendo-react-inputs';
import { Button } from '@progress/kendo-react-buttons';
import { useLocalization } from '@progress/kendo-react-intl';
import { enMessages } from '../../../../messages/en-US';
import { CauseCode } from './WorkOrdersTypes';

/** UI-only sentinel for the "No cause code" option (maps to onApply(null) → empty string for the backend). */
const NONE_CODE = '__none__';

/** Max length for the cause code comment. */
const COMMENT_MAX_LENGTH = 1000;

interface CauseCodeOption extends CauseCode {
  label: string;
}

interface WorkOrdersCauseCodeDialogProps {
  /** Number of tasks the chosen code will be applied to. */
  selectedCount: number;
  /** Point name shown when exactly one task is selected (e.g. opened from its chip). */
  selectedName?: string;
  /** Pre-selected cause code (single-task edit); null/undefined leaves the dropdown empty. */
  initialCauseCode?: CauseCode | null;
  /** Pre-filled comment (single-task edit). */
  initialComment?: string;
  causeCodes: CauseCode[];
  onClose: () => void;
  onApply: (causeCode: CauseCode | null, comment: string) => void;
}

export function WorkOrdersCauseCodeDialog({
  selectedCount,
  selectedName,
  initialCauseCode,
  initialComment,
  causeCodes,
  onClose,
  onApply,
}: WorkOrdersCauseCodeDialogProps) {
  const localizationService = useLocalization();
  const [selected, setSelected] = useState<CauseCodeOption | null>(null);
  const [comment, setComment] = useState('');

  const noneL = localizationService.toLanguageString(
    'custom.workOrdersCauseCodeNone',
    enMessages.custom.workOrdersCauseCodeNone
  );

  const options = useMemo<CauseCodeOption[]>(
    () => [
      { code: NONE_CODE, description: '', label: noneL },
      ...causeCodes.map((c) => ({ ...c, label: `${c.code}: ${c.description}` })),
    ],
    [causeCodes, noneL]
  );

  useEffect(() => {
    setSelected(initialCauseCode ? options.find((o) => o.code === initialCauseCode.code) ?? null : null);
    setComment(initialComment ?? '');
  }, [options, initialCauseCode, initialComment]);

  const titleL = localizationService.toLanguageString(
    'custom.workOrdersDetailEditCauseCode',
    enMessages.custom.workOrdersDetailEditCauseCode
  );
  const placeholderL = localizationService.toLanguageString(
    'custom.workOrdersCauseCodePlaceholder',
    enMessages.custom.workOrdersCauseCodePlaceholder
  );
  const selectedL = localizationService.toLanguageString(
    'custom.workOrdersDetailSelected',
    enMessages.custom.workOrdersDetailSelected
  );
  const hasCauseCode = !!selected && selected.code !== NONE_CODE;

  const cancelL = localizationService.toLanguageString('custom.cancel', enMessages.custom.cancel);
  const saveL = localizationService.toLanguageString('custom.save', enMessages.custom.save);
  const commentLabelL = localizationService.toLanguageString(
    'custom.workOrdersCauseCodeCommentLabel',
    enMessages.custom.workOrdersCauseCodeCommentLabel
  );
  const commentPlaceholderL = localizationService.toLanguageString(
    'custom.workOrdersCauseCodeCommentPlaceholder',
    enMessages.custom.workOrdersCauseCodeCommentPlaceholder
  );

  return (
    <Dialog title={titleL} onClose={onClose} width={420}>
      <div className="flex flex-col gap-3 p-1">
        <div>
          {selectedName && (
            <div className="text-sm font-medium break-words text-slate-800">{selectedName}</div>
          )}
          <div className="text-sm text-slate-500">{`${selectedCount} ${selectedL}`}</div>
        </div>
        <DropDownList
          data={options}
          textField="label"
          dataItemKey="code"
          value={selected}
          valueRender={(el, value) =>
            value?.code
              ? el
              : cloneElement(el, el.props, <span className="text-slate-400">{placeholderL}</span>)
          }
          onChange={(e: DropDownListChangeEvent) =>
            setSelected(e.value?.code ? (e.value as CauseCodeOption) : null)
          }
        />
        {hasCauseCode && (
          <label className="flex flex-col gap-1 text-sm text-slate-500">
            {commentLabelL}
            <TextArea
              rows={3}
              value={comment}
              maxLength={COMMENT_MAX_LENGTH}
              resizable="vertical"
              placeholder={commentPlaceholderL}
              onChange={(e: TextAreaChangeEvent) => setComment(String(e.value ?? ''))}
            />
            <span className="self-end text-xs text-slate-400">{`${comment.length} / ${COMMENT_MAX_LENGTH}`}</span>
          </label>
        )}
      </div>
      <DialogActionsBar>
        <Button onClick={onClose}>{cancelL}</Button>
        <Button
          themeColor="primary"
          disabled={!selected}
          onClick={() => {
            if (!selected) return;
            if (selected.code === NONE_CODE) {
              onApply(null, '');
              return;
            }
            const { label, ...causeCode } = selected;
            onApply(causeCode, comment);
          }}
        >
          {saveL}
        </Button>
      </DialogActionsBar>
    </Dialog>
  );
}
